import React, { useState } from 'react';
import { X, ArrowRight, Shield, Check, Loader2 } from 'lucide-react';

type Props = {
  open: boolean;
  onClose: () => void;
};

const DemoRequestModal: React.FC<Props> = ({ open, onClose }) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [mensagem, setMensagem] = useState('Quero agendar uma demonstração do Escalone.');
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nome || !email || !telefone) {
      setErro('Nome, email e telefone são obrigatórios');
      return;
    }

    setEnviando(true);
    setErro('');

    try {
      const isDev = window.location.port === '5173' || window.location.port === '5174';
      const apiBase = isDev ? 'http://localhost:5001' : `${window.location.protocol}//${window.location.hostname}`;
      const response = await fetch(`${apiBase}/api/demo/solicitar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nome,
          email,
          telefone,
          empresa,
          mensagem
        })
      });

      const data = await response.json();

      if (data.success) {
        setEnviado(true);
      } else {
        setErro(data.message || 'Erro ao enviar solicitação');
      }
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      setErro('Erro de conexão. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      {/* Decorative blurred bubbles matching the page design */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-blue-400/30 via-purple-400/30 to-indigo-400/30 blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-purple-400/30 via-pink-400/30 to-blue-400/30 blur-3xl animate-pulse" />
      </div>

      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white/90 backdrop-blur-xl border border-white/20 shadow-2xl">
        {/* Top gradient accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600" />
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200/50 bg-white/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white flex items-center justify-center font-bold text-lg shadow-lg">
              E
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Agendar demonstração</h3>
              <p className="text-sm text-gray-600">Transforme seus processos hoje mesmo</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!enviado ? (
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            {/* Badge de benefício */}
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700 mb-2">
              <Shield className="w-4 h-4" /> 
              Demonstração personalizada e gratuita
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome completo</label>
                <input 
                  value={nome} 
                  onChange={e => setNome(e.target.value)} 
                  required 
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/70 backdrop-blur-sm"
                  placeholder="Seu nome completo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email corporativo</label>
                <input 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  type="email" 
                  required 
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/70 backdrop-blur-sm"
                  placeholder="seu.email@empresa.com"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
                  <input 
                    value={telefone} 
                    onChange={e => setTelefone(e.target.value)} 
                    placeholder="(48) 99999-9999" 
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/70 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                  <input 
                    value={empresa} 
                    onChange={e => setEmpresa(e.target.value)} 
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/70 backdrop-blur-sm"
                    placeholder="Nome da sua empresa"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Como podemos ajudar?</label>
                <textarea 
                  value={mensagem} 
                  onChange={e => setMensagem(e.target.value)} 
                  rows={3} 
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/70 backdrop-blur-sm resize-none"
                  placeholder="Conte um pouco sobre seus processos e necessidades..."
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={enviando}
              className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed"
            >
              {enviando ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  Solicitar demonstração <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
            
            {erro && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {erro}
              </div>
            )}
            
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-4">
              <div className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                <span>Sem compromisso</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                <span>Suporte dedicado</span>
              </div>
            </div>
          </form>
        ) : (
          <div className="px-6 py-12 text-center space-y-6">
            {/* Success animation */}
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mx-auto flex items-center justify-center shadow-lg">
                <Check className="w-10 h-10 text-white" />
              </div>
              <div className="absolute inset-0 w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mx-auto animate-ping opacity-20" />
            </div>
            
            <div className="space-y-3">
              <h4 className="text-2xl font-bold text-gray-900">Solicitação enviada!</h4>
              <p className="text-gray-600 leading-relaxed max-w-sm mx-auto">
                Recebemos sua solicitação! Nossa equipe entrará em contato em até <strong>2 horas úteis</strong> 
                via email e WhatsApp para agendar sua demonstração personalizada.
              </p>
              <div className="text-sm text-blue-600 bg-blue-50 px-4 py-2 rounded-lg inline-block">
                📧 Verifique também sua caixa de spam
              </div>
            </div>
            
            <button 
              onClick={onClose} 
              className="rounded-xl border-2 border-gray-300 px-6 py-3 font-medium hover:border-gray-900 hover:bg-gray-50 transition-all"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRequestModal;
